import styled from 'styles/styled';

// Banner styles
export const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 0;

  @media (max-width: 720px) {
    margin-bottom: 20px;
  }
`;

export const CopyIconImage = styled.img`
  width: 20px;
  height: 20px;
  display: block;
`;

export const MobileBanner = styled.div`
  display: none;
  @media (max-width: 720px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
`;

export const DesktopBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  @media (max-width: 720px) {
    display: none;
  }
`;

export const BannerImage = styled.img`
  max-width: 100%;
  height: auto;
  object-fit: contain;
  max-height: 200px;
  opacity: 0.85;
  filter: drop-shadow(0 0 32px rgba(82, 211, 129, 0.08));

  @media (max-width: 720px) {
    max-height: 150px;
  }
`;

export const TimerText = styled.div<{ isWarning?: boolean }>`
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.isWarning ? '#FF6B6B' : '#52D381'};
  background: ${props => props.isWarning ? 'rgba(255, 107, 107, 0.1)' : 'rgba(82, 211, 129, 0.1)'};
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${props => props.isWarning ? '#FF6B6B' : '#52D381'};

  @media (max-width: 720px) {
    font-size: 14px;
    top: 8px;
    right: 12px;
    padding: 4px 8px;
  }
`;

// Form styles
export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 720px) {
    gap: 16px;
  }
`;

export const FormTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #EFEFEF;
  line-height: 1.2;
  letter-spacing: -0.01em;
  margin: 0;
  text-align: center;

  @media (max-width: 720px) {
    font-size: 22px;
  }
`;

export const SubtitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 auto;
`;

export const FormSubtitle = styled.p`
  font-size: 14px;
  color: #EFEFEF;
  font-weight: 300;
  margin: 0 auto;
  text-align: center;
  line-height: 1.4;
  max-width: 460px;
  word-wrap: break-word;

  @media (max-width: 720px) {
    font-size: 13px;
    max-width: 320px;
  }

  strong {
    color: #EFEFEF;
    font-weight: 700;
  }
`;

export const InfoTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const InfoIcon = styled.button`
  font-size: 10px;
  color: #EFEFEF;
  background: transparent;
  border: 1.5px solid #EFEFEF;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
`;

export const InfoTooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
  padding: 10px 12px;
  background: #292B2E;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: #EFEFEF;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  z-index: 2;
`;

export const InputWrapper = styled.div<{ filled?: boolean; hasError?: boolean; isValid?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  background: #292B2E;
  border-radius: 12px;
  padding: 0 8px 0 18px;
  /**
   * Border priority: error > valid (passes length-check, blurred) >
   * filled (sender purple) > transparent (empty). Focus override
   * lower down keeps purple while typing; bull green only signals
   * "address looks good" once the user has stepped away.
   */
  border: 1px solid
    ${({ filled, hasError, isValid }) =>
      hasError ? '#FF6782'
        : isValid ? '#52D381'
        : filled ? 'rgba(70, 66, 255, 0.4)'
        : 'transparent'};
  transition: border-color 200ms ease-in, box-shadow 200ms ease-in;

  &:focus-within {
    border-color: ${({ hasError }) => (hasError ? '#FF6782' : '#4642FF')};
    box-shadow: ${({ hasError }) =>
      hasError ? '0 0 0 4px rgba(255, 103, 130, 0.18)' : '0 0 0 4px rgba(70, 66, 255, 0.18)'};
  }

  @media (max-width: 720px) {
    padding: 0 6px 0 12px;
  }
`;

export const ChainPrefix = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-right: 10px;
  margin-right: 4px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #A7A8B2;
  flex-shrink: 0;
  height: 30px;
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const Input = styled.input<{ hasError?: boolean }>`
  flex: 1 1 auto;
  background: transparent;
  border: 0;
  outline: 0;
  color: #D9DDE4;
  font-family: 'Roboto Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 16px;
  height: 50px;
  min-width: 0;

  &::placeholder {
    color: #7A7E8C;
    font-family: inherit;
    font-weight: 300;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 720px) {
    font-size: 14px;
  }
`;

export const CopyIcon = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  background: transparent;
  border: 0;
  color: #A7A8B2;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  border-radius: 6px;
  flex-shrink: 0;
  transition: color 200ms ease-in, background 200ms ease-in;

  &:hover:not(:disabled) {
    color: #EFEFEF;
    background: rgba(255, 255, 255, 0.04);
  }

  @media (max-width: 720px) {
    padding: 0 8px;
    font-size: 12px;
  }
`;

export const ErrorText = styled.div`
  color: #FF6782;
  font-size: 11px;
  margin-top: 8px;
  line-height: 1.4;
  width: 100%;
  max-width: 460px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
`;

/**
 * Inline "looks valid" cue shown beneath the wallet input once the
 * user has typed something that passes the chain-specific length
 * check. Mirrors the help text spec from
 * trading-battles-design-system/project/preview/components-inputs.html
 * (.help.ok — bull green, 11/lh 1.4) but wraps a check svg + label.
 */
export const SuccessText = styled.div`
  color: #52D381;
  font-size: 11px;
  margin-top: 8px;
  line-height: 1.4;
  width: 100%;
  max-width: 460px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 24px;
  justify-content: center;

  @media (max-width: 720px) {
    gap: 12px;
    margin-top: 20px;
  }
`;

export const CancelButton = styled.button`
  width: 100%;
  min-height: 50px;
  padding: 10px 18px;
  border: 1px solid #535D70;
  background-color: transparent;
  color: #EFEFEF;
  border-radius: 10px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 200ms ease-in, border-color 200ms ease-in, color 200ms ease-in;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.04);
    border-color: #7A7E8C;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(83, 93, 112, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ButtonWrapper = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: 220px;
`;

/**
 * Belt — chain chip + countdown beneath the stepper. Per design app.css .belt.
 *
 * The `& > *` reset neutralises auto margins on direct children so
 * standalone components like TimerDisplay (which set `margin: 10px auto 16px`
 * for legacy single-element layout) don't claim all the horizontal space and
 * push siblings to the edges.
 */
export const Belt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;

  & > * {
    margin: 0;
  }
`;
